import React from "react"

import { Button, Grid, Typography } from "@material-ui/core"
import { ActionDialog, Notification } from "@/components"
import { FileCopyOutlined } from "@material-ui/icons"
import { Trackablelink } from "@/protocols/trackableLinks"
import { ErrorType } from "@/hooks/useValidation"

import ErrorHandlerService from "@/services/ErrorHandler"
import ApiService from "@/services/Api"
import copy from "copy-to-clipboard"
import useStyles from "@/pages/Admin/TrackableLinks/AdvertisementSetupDialog/styles"
import newColors from "@/styles/newColors"
import { mountTrackableLinkURL, mountTrackableLinkMetricsPagePath } from "@/utils/trackableLinks"
import { renderComponent } from "@/utils/node"
import { letalkLinks } from "@/utils/link"

type AdSetupDialogProps = {
	openDialog: boolean
	shouldRedirectToMetrics?: boolean
	title?: string;
	trackableLinkId: number
}

type AdSetupDialogType = {
	open: (props: AdSetupDialogProps) => void
}

// This string defines dynamic URL parameters for tracking ad performance.
// It uses template variables (e.g., {{site_source_name}}) that are replaced by the ad platform.
// Standard UTM parameters are included for analytics:
// - utm_source: where the traffic comes from
// - utm_medium: the ad placement (e.g., feed, stories)
// - utm_campaign: the name of the campaign
// - utm_content: the specific ad set and ad name
// - utm_term: repeats the placement, useful for keyword-level tracking
// Additionally, a custom parameter 'letalk_fbid' combines ad ID, source, and placement for internal tracking.
const URL_PARAMETERS =
"utm_source={{site_source_name}}&utm_medium={{placement}}&utm_campaign={{campaign.name}}&utm_content={{adset.name}}_{{ad.name}}&utm_term={{placement}}&letalk_fbid={{ad.id}}_{{site_source_name}}_{{placement}}"

const AdvertisementSetupDialog: AdSetupDialogType & React.FC<AdSetupDialogProps> = (props) => {
	const {
		openDialog,
		shouldRedirectToMetrics = true,
		title = "NOVO LINK CRIADO COM SUCESSO! ✅",
		trackableLinkId
	} = props

	const classes = useStyles()
	const linkToRedirectToMetrics = mountTrackableLinkMetricsPagePath(trackableLinkId?.toString())

	const [loading, setLoading] = React.useState(false)
	const [trackableLink, setTrackableLink] = React.useState<Trackablelink>({} as Trackablelink)
	const [isOpenAdvertisementSetupDialog, setIsOpenAdvertisementSetupDialog] = React.useState(openDialog)

	const handleToRedirectTrackableLinkMetrics = () => {
		window.location.href = linkToRedirectToMetrics
	}

	const handleCloseAdvertisementSetupDialog = () => {
		setIsOpenAdvertisementSetupDialog(false)
	}

	const handleGetTrackableLinkById = async () => {
		setLoading(true)
		try {
			const { data } = await ApiService.get<{ trackablelink: Trackablelink }>(`/trackable-links/${trackableLinkId}`)
			setLoading(false)

			setTrackableLink(data.trackablelink)
			return data.trackablelink
		} catch (error) {
			setLoading(false)
			Notification.error({ message: "Ocorreu um erro ao buscar o link" })

			ErrorHandlerService.handle(error as ErrorType)
		}
	}

	const linkToCopy = mountTrackableLinkURL(trackableLink.hash, trackableLink.slug)

	const copyHandler = (content: string) => {
		copy(content)
		Notification.success({ message: "Copiado com sucesso!" })
	}

	React.useEffect(() => {
		if (trackableLinkId) {
			handleGetTrackableLinkById()
		}
	}, [trackableLinkId])

	return (
		<ActionDialog
			maxWidth="sm"
			openDialog={isOpenAdvertisementSetupDialog}
			onSave={shouldRedirectToMetrics ? handleToRedirectTrackableLinkMetrics : handleCloseAdvertisementSetupDialog}
			onClose={shouldRedirectToMetrics ? handleToRedirectTrackableLinkMetrics : handleCloseAdvertisementSetupDialog}
			loading={loading}
			cancelText="FAREI MAIS TARDE"
			saveText="JÁ FIZ"
			title={title}
		>
			<Grid container direction="column" spacing={4}>
				<Grid item>
					<Typography variant="subtitle1">
						Basta seguir o passo a passo abaixo para finalizar a configuração do seu link rastreável. Vamos nessa! 🚀
					</Typography>
				</Grid>
				<Grid item>
					<Grid container spacing={4}>
						<Grid item xs={6}>
							<Grid container spacing={2}>
								<Grid item>
									<Grid container spacing={1}>
										<Grid item>
											<Typography variant="h5" className={classes.subtitle}>Passo 1</Typography>
											<Typography variant="body2">
												Clique no botão abaixo para copiar o link gerado e cole como destino do anúncio no Meta Ads.{" "}
												<a
													className={classes.link}
													href={letalkLinks.wikiHowToAddATrackableLinkAsAnAdversetimentDestinationInMetaADS}
													rel="noopener noreferrer"
													target="_blank"
												>
													Como fazer isso?
												</a>
											</Typography>
										</Grid>
									</Grid>
								</Grid>
								<Grid item xs={12}>
									<Button
										className={classes.button}
										fullWidth
										variant="contained"
										color="primary"
										onClick={() => copyHandler(linkToCopy)}
										startIcon={
											<FileCopyOutlined
												style={{
													fontSize: 16,
													color: newColors.purple[500]
												}}
											/>
										}
									>
										Copiar link
									</Button>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={6}>
							<Grid container spacing={2}>
								<Grid item>
									<Grid container spacing={1}>
										<Grid item>
											<Typography variant="h5" className={classes.subtitle}>Passo 2</Typography>
											<Typography variant="body2">
												Clique no botão abaixo para copiar os parâmetros de URL e adicione-os dentro do Meta Ads.{" "}
												<a
													className={classes.link}
													href={letalkLinks.wikiHowToAddURLParametersWithinTheAdvertisementInMetaADS}
													rel="noopener noreferrer"
													target="_blank"
												>
													Como fazer isso?
												</a>
											</Typography>
										</Grid>
									</Grid>
								</Grid>
								<Grid item xs={12}>
									<Button
										className={classes.button}
										fullWidth
										variant="contained"
										color="primary"
										startIcon={
											<FileCopyOutlined
												style={{ fontSize: 16, color: newColors.purple[500] }}
											/>
										}
										onClick={() => copyHandler(URL_PARAMETERS)}
									>
										Copiar parâmetros
									</Button>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</ActionDialog>
	)
}

AdvertisementSetupDialog.open = (props: AdSetupDialogProps) => {
	renderComponent(
		"advertisement-setup-dialog",
		<AdvertisementSetupDialog
			{...props}
		/>
	)
}

export default AdvertisementSetupDialog
